import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { User } from "@shared/models/user"
import { analytics } from "cc-frontend/error-handling"
import type EmberStore from "cc-frontend/services/ember-store"
import type NotifierService from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import type SessionStore from "cc-frontend/services/session-store"
import type SocketService from "cc-frontend/services/socket"
import type Stripe from "cc-frontend/services/stripe"
import Cookies from "js-cookie"

interface Sig {
  Args: {
    class: string
    buttonTitle: string
    buttonSubtitle: string
    plan: string
    price: string
    value: string
    coupon?: string
  }
}

export default class StripeUpgrade extends Component<Sig> {
  @service declare stripe: Stripe
  @service declare session: Session
  @service declare socket: SocketService
  @service declare rpc: Rpc
  @service("ember-store") declare store: EmberStore
  @service declare notifier: NotifierService
  @service declare sessionStore: SessionStore

  @tracked
  isUpgrading = false

  /**
   * The Stripe coupon to charge against. An explicit `?coupon=` is a deliberate campaign
   * link, so it outranks the discount a partner referral happens to carry.
   */
  get coupon(): string {
    if (this.args.coupon) return this.args.coupon
    // Dub's script leaves the referring partner's discount in this cookie on arrival,
    // already resolved to a Stripe coupon — there is nothing to look up.
    const partnerCoupon = Cookies.getJSON("dub_partner_data")?.discount?.couponId
    return this.sessionStore.fetchObject<{ coupon: string }>("coupon")?.coupon || partnerCoupon || ""
  }

  @action
  async click() {
    // analytics.track("Upgrade - Open Modal")
    const user = this.session.user.content as User
    ;(await get(this, "stripe").handler).open({
      description: `Pro Teacher Account ${this.args.price}`,
      panelLabel: `Start Free 30 day Trial (${this.args.price} afterwards)`,
      email: user.attributes.email,
      token: (token) => {
        this.isUpgrading = true

        const data = {
          userId: user.id,
          source: token.id,
          plan: this.args.plan,
          coupon: this.coupon,
        }

        this.rpc
          .user_upgrade_with_card(data)
          .then((response) => {
            if (response.responseOk) {
              this.onSuccess(user.id)
            } else {
              if (response.result.contactSupport) {
                // Something went wrong that they can't fix on their own.
                this.notifier.warning("", {
                  notificationId: user.id,
                  title: response.result.message,
                  message: "Contact us through the message box below.",
                  duration: 5000,
                  type: "is-processing",
                })
                if (window && window.Intercom) {
                  window.Intercom(
                    "showNewMessage",
                    `I just tried to upgrade and something went wrong.`
                  )
                }
              } else {
                // Something went wrong that they can retry - usually a card error.
                this.notifier.warning("", {
                  notificationId: user.id,
                  title: response.result.message,
                  message: "Please try again.",
                  duration: 5000,
                  type: "is-processing",
                })
              }
            }
          })
          .catch((error: unknown) => {
            console.error(error)
            // Something went wrong that they can't fix on their own.
            this.notifier.warning("", {
              notificationId: user.id,
              title: "Something went wrong with the upgrade.",
              message: "Contact us through the message box below.",
              duration: 5000,
              type: "is-processing",
            })
            if (window && window.Intercom) {
              window.Intercom("showNewMessage", `I just tried to upgrade and something went wrong.`)
            }
          })
          .finally(() => {
            this.isUpgrading = false
          })
      },
    })
  }

  onSuccess(userId: string) {
    analytics.track("Subscription - Started Trial on Client")
    this.sessionStore.clear("coupon")

    this.socket.notifyUpgradeToPro({ userId })!.then(() => {
      window.location.reload()
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "User::StripeUpgrade": typeof StripeUpgrade
  }
}
